import { Component, ElementRef, OnDestroy, OnInit, ViewChild, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalController } from '@ionic/angular';
import { EventProvider } from 'src/app/services/event';
import { ToastService } from 'src/app/services/toast.service';
import { AppConstants, ObservationEvents } from 'src/app/utilities/constant';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit, OnDestroy {

  // Declared default strings using @Input decorator, so they can be overridden
  @Input() uploadTitle: string;
  @Input() downloadLinkText: string;
  @Input() downloadLinkUrl: string = 'assets/sample-template.pdf';
  @Input() dragDropText: string;
  @Input() doneButtonText: string;
  @Input() showDownloadLink: boolean = true;
  @Input() subHeader: string;
  @Input() showSubHeader: boolean = true; // Option to show or hide subheader
  @Input() type: string = '';
  @Input() sampleDownload: boolean = false;
  @Input() acceptTypes: string = '';
  @Input() hideDownloadLink: boolean = false;
  @ViewChild('fileInput', { static: false }) fileInput: ElementRef;
  fileName: string = '';  // To store the selected file name
  uploadProgress: number = 0;  // To track the upload progress
  public subs = new SubSink();
  public isErrorOccurred: boolean;
  public isFileUploaded: boolean;
  public isValidationError: boolean = false;  // Track validation errors separately
  private successCloseTimeout: ReturnType<typeof setTimeout>;
  private errorToastShown = false;
  constructor(private modalController: ModalController,
    private eventProvider: EventProvider,
    private toastService: ToastService,
  private translate: TranslateService) { }

  ngOnInit(): void {
    this.uploadTitle = this.uploadTitle || this.translate.instant('Upload File');
    this.downloadLinkText = this.downloadLinkText || this.translate.instant('Download Sample Template');
    this.dragDropText = this.dragDropText || this.translate.instant('Click to choose file');
    this.doneButtonText = this.doneButtonText || this.translate.instant('Done');
    this.subHeader = this.subHeader || this.translate.instant('Please upload file in specified format.');
  
    this.subs.sink = this.eventProvider.event(ObservationEvents.ERROR_OCCURRED).subscribe((res) => {
      console.log(res, 'ERROR_OCCURRED');
      if (this.isFileUploaded) {
        return;
      }
      this.errorToastShown = res?.toastShown === true;
      this.isErrorOccurred = true;
    })
    this.subs.sink = this.eventProvider.event(ObservationEvents.FILE_UPLOAD_SUCCESS).subscribe((res) => {
      console.log(res, 'FILE_UPLOAD_SUCCESS');
      this.isErrorOccurred = false;
      this.isValidationError = false;
      this.isFileUploaded = true;
      this.uploadProgress = 100;
      clearTimeout(this.successCloseTimeout);
      this.successCloseTimeout = setTimeout(() => {
        this.onDone();
      }, 700);
    })
    
    // Listen for validation errors
    this.subs.sink = this.eventProvider.event(ObservationEvents.FILE_VALIDATION_ERROR).subscribe((res) => {
      console.log(res, 'FILE_VALIDATION_ERROR');
      this.isValidationError = true;
      this.isErrorOccurred = true;
      this.uploadProgress = 100; // Set progress to 100% for error state
      // Close modal after 2 seconds
      setTimeout(() => {
        this.closeModal();
      }, 2000);
    })
  }

  ngOnDestroy(): void {
    clearTimeout(this.successCloseTimeout);
    this.subs.unsubscribe();
  }

  /**
   * Triggers file input
   */
  public triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  /**
   * Determines whether file selected on
   * 
   * @param event 
   */
  public onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input && input.files && input.files.length > 0) {
    const file = input.files[0];
    this.resetUploadState();
    const allowedExtensions = this.acceptTypes.replace(/\s/g, '').split(',');
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.toastService.presentToast(
        this.translate.instant('file_size_exceed_5mb'),
        4000,
        AppConstants.ERROR_SNACKBAR,
        'top'
      );
      input.value = '';
      return;
    }

    if (!allowedExtensions.includes(fileExtension)) {
      const message = this.getAcceptTypesMessage(this.acceptTypes);
      this.toastService.presentToast(
        this.translate.instant(message),
        4000,
        AppConstants.ERROR_SNACKBAR,
        'top'
      );
      input.value = '';
      return;
    }
    this.fileName = file.name;
    this.uploadFile(event);
  }
}

  /**
   * Returns the appropriate message based on the accepted file types
   * 
   * @param acceptTypes 
   * @returns string
   */
  private getAcceptTypesMessage(acceptTypes: string): string {
    if (!acceptTypes) return '';
    const types = acceptTypes.replace(/\s/g, '').split(',');
    if (types.includes('.pdf') && (types.includes('.doc') || types.includes('.docx'))) {
      return this.translate.instant('only_pdf_or_doc_allowed');
    }
    if (types.includes('.xls') || types.includes('.xlsx') || types.includes('.csv')) {
      return this.translate.instant('only_excel_or_csv_allowed');
    }
    if (types.includes('.pdf')) {
      return this.translate.instant('only_excel_or_csv_allowed');
    }
    return this.translate.instant('selected_file_type_not_allowed');
  }

  /**
   * Uploads file
   * 
   * @param event 
   */
  public uploadFile(event) {
    this.uploadProgress = 0;
    this.eventProvider.BroadcastEvent(ObservationEvents.FILE_UPLOAD, event);
    const interval = setInterval(() => {
      if (this.uploadProgress < 90 && !this.isErrorOccurred && !this.isFileUploaded) {
        this.uploadProgress += 10;
      }
      // If a validation error occurs, the event handler already handles it
      if (this.isValidationError) {
        clearInterval(interval);
        return;
      }
      // If a general error occurs, clear the interval
      if (this.isErrorOccurred && !this.isFileUploaded) {
        this.uploadProgress += 20;
        clearInterval(interval);
        if (!this.errorToastShown) {
          this.toastService.presentToast(this.translate.instant('error_occurred'), 4000, AppConstants.ERROR_SNACKBAR, 'top');
        }
        this.closeModal();
      }
      // If the file is uploaded, progress bar should jump to 100%
      if (this.isFileUploaded) {
        this.uploadProgress = 100;
        clearInterval(interval);
      }
    }, 300);
  }

  private resetUploadState(): void {
    clearTimeout(this.successCloseTimeout);
    this.isErrorOccurred = false;
    this.errorToastShown = false;
    this.isFileUploaded = false;
    this.isValidationError = false;
    this.uploadProgress = 0;
  }


  /**
   * Determines whether done on
   */
  public onDone() {
    clearTimeout(this.successCloseTimeout);
    this.modalController.dismiss({
      isActionConfirmed: true,
      dismissed: true,
    });
  }

  /**
   * Closes modal
   */
  public closeModal() {
    clearTimeout(this.successCloseTimeout);
    this.modalController.dismiss({
      isActionConfirmed: false,
      dismissed: true,
    });
  }

  /**
   * Determines whether click download link on
   */
  public onClickDownloadLink() {
   this.eventProvider.BroadcastEvent(ObservationEvents.DOWNLOAD_FILE, this.downloadLinkText);
  }
}
